import React from "react";
import { useAiAccess } from "../../hooks/useAiAccess";
import { MyAiAccess } from "../../types/Ai";
import classes from "./aiCost.module.scss";

type CreditAccess = Pick<MyAiAccess, "fastPerDay" | "smartPerDay" | "usedFast" | "usedSmart"> | null | undefined;

/** Credits left today in a mode (null = no daily limit). */
export const creditsLeft = (access: CreditAccess, mode: "fast" | "smart" = "fast"): number | null => {
    const limit = mode === "smart" ? access?.smartPerDay : access?.fastPerDay;
    if (limit === null || limit === undefined) return null;
    return Math.max(0, limit - ((mode === "smart" ? access?.usedSmart : access?.usedFast) ?? 0));
};

/** "AI · 1 credit · 27 left today" (or "no credits left today" / "no daily limit"). */
export const creditText = (access: CreditAccess, mode: "fast" | "smart" = "fast", cost: string = "1 credit"): string => {
    const left = creditsLeft(access, mode);
    if (left === 0) return "AI · no credits left today";
    return `AI · ${cost} · ${left === null ? "no daily limit" : `${left} left today`}`;
};

interface AiCostProps {
    /** Pass when the caller already has the access (otherwise it's read here). */
    access?: CreditAccess;
    mode?: "fast" | "smart";
    /** What this action uses, e.g. "1 credit" or "only if needed". */
    cost?: string;
    /** Just "AI · N left" — for buttons that open Ask (the credit is used when you ask). */
    compact?: boolean;
    /** Sits inside its button — the AI mark and the credit are part of the button (user, 2026-10-13). */
    inButton?: boolean;
    /** Leave out the sparkle when the button's own icon already is it. */
    noIcon?: boolean;
}

const AiCostWith: React.FC<AiCostProps & { value: CreditAccess }> = ({ value, mode = "fast", cost, compact, inButton, noIcon }) => {
    if (!value) return null;
    const left = creditsLeft(value, mode);
    return (
        <span className={`${classes.cost} ${inButton ? classes.inButton : ""} ${left === 0 ? classes.none : ""}`} data-ai-mark={inButton || undefined}
            title="This uses AI. Each AI action uses one of your daily AI credits; they reset at midnight. Anything read on this computer uses none.">
            {!noIcon && <><i className="bi bi-stars" aria-hidden="true" /> </>}{compact ? (left === 0 ? "AI · no credits left" : `AI · ${left === null ? "no limit" : `${left} left`}`) : creditText(value, mode, cost)}
        </span>
    );
};

const AiCostFromHook: React.FC<AiCostProps> = (props) => {
    const { access } = useAiAccess();
    return <AiCostWith {...props} value={access} />;
};

/**
 * Marks an action as AI and shows what it uses and what's left today — on
 * every AI button (user, 2026-10-13: "indicate it is AI and show the credit").
 */
/**
 * The mark on a button whose action runs on this computer (no AI, no credit) —
 * beside the AI credit mark it says plainly that nothing goes to the AI.
 */
export const FreeMark: React.FC<{ inButton?: boolean }> = ({ inButton }) => (
    <span className={`${classes.cost} ${inButton ? classes.inButton : ""}`} title="Read on this computer — nothing is sent to the AI and no credit is used">
        free · on this computer
    </span>
);

const AiCost: React.FC<AiCostProps> = (props) =>
    props.access !== undefined ? <AiCostWith {...props} value={props.access} /> : <AiCostFromHook {...props} />;

export default AiCost;
